import { API_CONFIG, API_ENDPOINTS } from '../lib/api/config';

export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

export interface TokenRefreshResponse {
  success: boolean;
  data?: {
    token: string;
  };
  error?: string;
}

export interface RefreshTokenData {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

class TokenRefreshService {
  private isRefreshing = false;
  private refreshPromise: Promise<RefreshOutcome> | null = null;
  private refreshSubscribers: Array<(outcome: RefreshOutcome) => void> = [];
  private lastRefreshRequestId: string | null = null;

  public getLastRefreshRequestId(): string | null {
    return this.lastRefreshRequestId;
  }

  private isTokenExpired(token: string, bufferMinutes: number = 5): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = payload.exp;
      
      return (expirationTime - currentTime) < (bufferMinutes * 60);
    } catch (error) {
      return true; // If we can't parse it, consider it expired
    }
  }

  public getTokenUserId(token: string): string | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.userId ?? null;
    } catch (error) {
      return null;
    }
  }

  public getTokenExpirationTime(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      return 0;
    }
  }

  private async refreshAccessToken(): Promise<RefreshOutcome> {
    const refreshToken = localStorage.getItem('refresh_token');

    const requestId =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `refresh-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.lastRefreshRequestId = requestId;

    if (!refreshToken) {
      console.error(
        '[TokenRefreshService] No refresh_token in storage — cannot attempt refresh.',
        { requestId }
      );
      return 'rejected';
    }

    console.info('[TokenRefreshService] Refresh attempt started', { requestId });

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status >= 500 || errorData.code === 'SESSION_CHECK_UNAVAILABLE') {
          console.error(
            '[TokenRefreshService] /auth/refresh could not verify the session — BACKEND/DB PROBLEM, not an auth rejection. The user is NOT being logged out. Root cause is in dbErrorCode:',
            {
              requestId,
              httpStatus: response.status,
              code: errorData.code,
              dbErrorCode: errorData.dbErrorCode ?? '(not reported)',
              error: errorData.error || errorData.message,
            }
          );
          return 'unavailable';
        }

        console.error(
          '[TokenRefreshService] /auth/refresh failed — this is the exact reason the frontend will log the user out:',
          {
            requestId,
            httpStatus: response.status,
            code: errorData.code,
            error: errorData.error || errorData.message,
          }
        );
        return 'rejected';
      }

      const data: TokenRefreshResponse = await response.json();

      if (data.success && data.data?.token) {
        localStorage.setItem('auth_token', data.data.token);

        const expiresAt = this.getTokenExpirationTime(data.data.token);
        localStorage.setItem('token_expires_at', expiresAt.toString());

        console.info('[TokenRefreshService] Refresh succeeded', { requestId });
        return 'refreshed';
      } else {
        console.error(
          '[TokenRefreshService] /auth/refresh returned success:false without a hard error status',
          { requestId, data }
        );
        return 'rejected';
      }
    } catch (error) {
      console.error(
        '[TokenRefreshService] /auth/refresh threw (network error / request never reached the backend):',
        { requestId, error }
      );
      return 'unavailable';
    }
  }

  private async handleTokenRefresh(): Promise<RefreshOutcome> {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }

    this.isRefreshing = true;
    this.refreshPromise = this.refreshAccessToken();

    try {
      const outcome = await this.refreshPromise;

      this.refreshSubscribers.forEach(callback => callback(outcome));
      this.refreshSubscribers = [];

      return outcome;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async ensureValidToken(): Promise<boolean> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      return false;
    }

    if (this.isTokenExpired(token)) {
      return (await this.handleTokenRefresh()) === 'refreshed';
    }

    return true;
  }

  async forceRefresh(): Promise<RefreshOutcome> {
    return await this.handleTokenRefresh();
  }

  clearTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user');
    localStorage.removeItem('onboarding_survey_completed');
    localStorage.removeItem('new_signup');
    localStorage.removeItem('selectedBusinessAccountId');
  }

  storeTokens(tokenData: RefreshTokenData): void {
    localStorage.setItem('auth_token', tokenData.token);
    localStorage.setItem('refresh_token', tokenData.refreshToken);
    localStorage.setItem('token_expires_at', tokenData.expiresAt.toString());
  }

  getTokenInfo(): { token: string | null; refreshToken: string | null; expiresAt: number | null } {
    const token = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const expiresAtStr = localStorage.getItem('token_expires_at');
    const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null;

    return { token, refreshToken, expiresAt };
  }

  shouldLogout(): boolean {
    const { token, refreshToken } = this.getTokenInfo();
    
    if (!token && !refreshToken) {
      return true;
    }

    if (!token && refreshToken) {
      return false; // Let the refresh process handle it
    }

    if (token && !refreshToken && this.isTokenExpired(token, 0)) {
      return true;
    }

    return false;
  }

  setupAutoRefresh(): void {
    const { token } = this.getTokenInfo();
    
    if (!token) {
      return;
    }

    const expiresAt = this.getTokenExpirationTime(token);
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    
    const refreshTime = Math.max(timeUntilExpiry - (5 * 60 * 1000), 60000); // At least 1 minute from now
    
    if (refreshTime > 0) {
      
      setTimeout(async () => {
        await this.ensureValidToken();
        this.setupAutoRefresh();
      }, refreshTime);
    }
  }
}

export const tokenRefreshService = new TokenRefreshService();

export { TokenRefreshService };
